// @systeme: auth-identite
// ============================================================================
// CECODEV Platform - Reset Password Page
// ============================================================================

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import { api, codeApi, messageApi } from "../lib/api";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorIsTokenIssue, setErrorIsTokenIssue] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const isFirstSetup = searchParams.get("setup") === "1";
  const setupPrenom = searchParams.get("prenom");
  const setupOrg = searchParams.get("org");

  useEffect(() => {
    if (!token) {
      setError("Token de réinitialisation manquant");
    }
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Token de réinitialisation manquant");
      return;
    }

    // Validation
    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }

    setIsLoading(true);

    try {
      await api.post("/auth/reset-password", {
        token,
        password,
      });

      setIsSuccess(true);
      toast.success(isFirstSetup ? "Mot de passe créé avec succès !" : "Mot de passe réinitialisé avec succès !");
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error: unknown) {
      const errorMessage = messageApi(error, "Erreur lors de la réinitialisation");
      const errorCode = codeApi(error);
      setError(errorMessage);
      setErrorIsTokenIssue(["INVALID_TOKEN", "ALREADY_USED", "EXPIRED"].includes(errorCode || ""));
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }

  if (!token) {
    return (
      <Box className="auth-background">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            px: 2,
          }}
        >
          <Card sx={{ width: "100%", maxWidth: 480 }}>
            <CardContent sx={{ p: 5, textAlign: "center" }}>
              <Typography variant="h5" sx={{ mb: 2, color: "error.main" }}>
                Lien invalide
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Ce lien de réinitialisation est invalide ou a expiré.
              </Typography>
              <Link
                to="/forgot-password"
                style={{
                  color: "#4BB7A5",
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                Demander un nouveau lien
              </Link>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="auth-background">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          px: 2,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 480,
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          }}
          className="fade-in"
        >
          <CardContent sx={{ p: 5 }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box className="hex-icon-container" sx={{ mb: 2, justifyContent: "center" }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "white" }}>
                  C
                </Typography>
              </Box>
              <Typography
                variant="h3"
                className="gradient-text"
                sx={{ fontWeight: 700, mb: 1 }}
              >
                CECODEV
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {isFirstSetup
                  ? setupPrenom ? `Bienvenue ${setupPrenom} !` : "Créez votre mot de passe"
                  : "Nouveau mot de passe"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isFirstSetup
                  ? `Définissez votre mot de passe pour accéder à votre espace${setupOrg ? ` (${setupOrg})` : ""}.`
                  : "Choisissez un nouveau mot de passe sécurisé"}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: errorIsTokenIssue ? 1 : 0 }}>{error}</Typography>
                {errorIsTokenIssue && (
                  <Link to="/forgot-password" style={{ color: "#4BB7A5", fontWeight: 600, textDecoration: "none", fontSize: "0.875rem" }}>
                    Demander un nouveau lien →
                  </Link>
                )}
              </Alert>
            )}

            {isSuccess ? (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {isFirstSetup
                    ? "Votre mot de passe a été créé avec succès !"
                    : "Votre mot de passe a été réinitialisé avec succès !"}
                </Typography>
                <Typography variant="body2">
                  Redirection vers la page de connexion...
                </Typography>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <TextField
                    label="Nouveau mot de passe"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    fullWidth
                    placeholder="••••••••"
                    autoComplete="new-password"
                    autoFocus
                    helperText="Au moins 8 caractères"
                  />

                  <TextField
                    label="Confirmer le mot de passe"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    fullWidth
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={isLoading}
                    sx={{ mt: 2 }}
                  >
                    {isLoading ? (
                      <>
                        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                        {isFirstSetup ? "Création..." : "Réinitialisation..."}
                      </>
                    ) : (
                      isFirstSetup ? "Créer mon mot de passe" : "Réinitialiser le mot de passe"
                    )}
                  </Button>
                </Stack>
              </form>
            )}

            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Link
                to="/login"
                style={{
                  color: "#4BB7A5",
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                Retour à la connexion
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}


